import getDataFromAtlassianMixin from '@/mixins/getDataFromAtlassian'
import * as Sentry from '@sentry/browser'
export default {
  mixins: [ getDataFromAtlassianMixin ],
  computed: {
    availableRequirements () {
      if (this.selectedRequirementType && this.selectedRequirementType.level) {
        const allowedTypeIds = [this.selectedRequirementType.level + 1, this.selectedRequirementType.level - 1]
        return this.requirements.filter(requirement => allowedTypeIds.includes(requirement.level))
      } else {
        return []
      }
    },
    sortedIssues () {
      return this.issues.sort((a, b) => a.id - b.id)
    },
    loading () {
      return this.issuesLoading || this.contentsLoading || this.statusesLoading || this.requirementsLoading || this.componentsLoading || this.assigneesLoading || this.attributesLoading
    },
    nameState () {
      return this.name.length > 0
    },
    nameInvalidFeedback () {
      if (this.name.length === 0) {
        return 'Required'
      }
      return ''
    },
    requirementTypeInvalidFeedback () {
      if (!this.selectedRequirementType) {
        return 'Required'
      }
      return ''
    },
    requirementTypeState () {
      return this.selectedRequirementType !== null
    },
    shouldDisableIssuesMultiselect () {
      return this.sortedIssues.length === 0 && !this.issuesLoading
    },
    currentProjectIsGlobal () {
      return this.currentProject && this.currentProject.id === 0
    }
  },
  data () {
    return {
      assignees: [],
      assigneesLoading: false,
      attributes: {},
      attributesLoading: false,
      attachments: [],
      components: [],
      contents: [],
      contentsLoading: false,
      componentsLoading: false,
      description: '',
      displayID: null,
      issues: [],
      issuesLoading: false,
      id: null,
      name: '',
      requirements: [],
      requirementsLoading: false,
      requirementTypes: [],
      requirementTypesLoading: false,
      selectedAssignee: null,
      selectedAttributes: {},
      selectedComponents: [],
      selectedContents: [],
      selectedIssueIds: [],
      selectedIssues: [],
      selectedRequirements: [],
      selectedRequirementType: null,
      selectedStatus: null,
      statuses: [],
      statusesLoading: false,
      submitting: false
    }
  },
  methods: {
    customIssueLabel ({ key, fields }) {
      return `${key}: ${fields.summary}`
    },
    customRequirementLabel (requirement) {
      /* eslint-disable camelcase */
      const idToShow = requirement.display_id ? requirement.display_id : requirement.requirement_id
      return `${requirement.prefix}${idToShow}: ${requirement.name}`
      /* eslint-enable camelcase */
    },
    getAssignees () {
      this.assigneesLoading = true
      window.AP.request({
        url: `/rest/api/3/user/assignable/search?maxResults=1000&project=${this.currentProject.key}`,
        success: response => {
          this.assignees = JSON.parse(response)
          this.assigneesLoading = false
        },
        error: (response) => {
          window.AP.flag.create({
            title: 'Error',
            body: 'There was a problem loading assignee data from Atlassian.  Please contact rmCloud support via the "Get help" button',
            type: 'error'
          })
          Sentry.captureMessage(response)
        }
      })
    },
    getAttributes () {
      this.attributesLoading = true
      this.$http.get(
        `api/attribute?atlassian_project_id=${this.currentProject.id}`
      ).then(response => {
        this.attributes = response.data.attributes
        this.attributesLoading = false
      }, response => {
        window.AP.flag.create({
          title: 'Error',
          body: 'There was a problem loading custom attributes.  Please contact rmCloud support via the "Get help" button',
          type: 'error'
        })
      })
    },
    getOptionsArray (options) {
      const result = []
      for (let option of options) {
        result.push({
          value: option.attribute_option_id, text: option.name
        })
      }
      return result
    },
    getComponents () {
      this.componentsLoading = true
      window.AP.request({
        url: `/rest/api/3/project/${this.currentProject.id}/components`,
        success: response => {
          this.components = JSON.parse(response)
          this.componentsLoading = false
        },
        error: (response) => {
          window.AP.flag.create({
            title: 'Error',
            body: 'There was a problem loading component data from Atlassian.  Please contact rmCloud support via the "Get help" button',
            type: 'error'
          })
          Sentry.captureMessage(response)
        }
      })
    },
    getContents () {
      this.contentsLoading = true
      this.$http.get(
        'api/confluence/content'
      ).then(response => {
        this.contents = response.data.results
        this.contentsLoading = false
      }, response => {
        window.AP.flag.create({
          title: 'Error',
          body: 'There was a problem loading confluence data from Atlassian.  Please contact rmCloud support via the "Get help" button',
          type: 'error'
        })
      })
    },
    getRequirements () {
      this.requirementsLoading = true
      this.$http.get(
        '/api/requirement?atlassian_project_id=' + this.currentProject.id
      ).then(response => {
        this.requirements = response.data.requirements
        this.requirementsLoading = false
      }, response => {
        window.AP.flag.create({
          title: 'Error',
          body: 'There was a problem loading requirement data from rmCloud.  Please contact rmCloud support via the "Get help" button',
          type: 'error'
        })
      })
    },
    getRequirementTypes () {
      this.requirementTypesLoading = true
      this.$http.get(
        '/api/requirement/type'
      ).then(response => {
        this.requirementTypes = response.data.requirement_types
        this.requirementTypesLoading = false
      }, response => {
        this.requirementTypesLoading = false
        window.AP.flag.create({
          title: 'Error',
          body: 'There was a problem loading requirement type data from rmCloud.  Please contact rmCloud support via the "Get help" button',
          type: 'error'
        })
      })
    },
    getRequirementsPlaceholder () {
      if (this.selectedRequirementType) {
        return `Associate with requirement(s)`
      } else {
        return 'Please select a requirement type above.'
      }
    },
    getIssues () {
      this.issues = []
      this.issuesLoading = true

      let maxResults = 100
      const payload = {
        'fields': ['summary'],
        'fieldsByKeys': false,
        'maxResults': maxResults
      }
      if (!this.currentProjectIsGlobal) {
        payload['jql'] = `project = "${this.currentProject.key}"`
      }

      let makeIssueRequest = (nextPageToken = null) => {
        if (nextPageToken) {
          payload['nextPageToken'] = nextPageToken
        } else {
          delete payload['nextPageToken']
        }
        window.AP.request({
          url: '/rest/api/3/search/jql',
          type: 'POST',
          contentType: 'application/json',
          data: JSON.stringify(payload),
          success: response => {
            let issueResponse = JSON.parse(response)
            if (issueResponse.issues) {
              this.issues.push.apply(this.issues, issueResponse.issues)
            }
            // Check if there are more pages
            if (issueResponse.nextPageToken) {
              makeIssueRequest(issueResponse.nextPageToken)
            } else {
              // Done paginating
              this.selectedIssues = this.issues.filter(issue => this.selectedIssueIds.includes(Number.parseInt(issue.id)))
              this.issuesLoading = false
            }
          },
          error: (response) => {
            this.$ga.event('error', 'getIssues', 'api-error', 1)
            Sentry.captureMessage(response)
            window.AP.flag.create({
              title: 'Error',
              body: 'There was a problem loading issue data from Atlassian.  Please contact rmCloud support via the "Get help" button',
              type: 'error'
            })
            this.issuesLoading = false
          }
        })
      }

      makeIssueRequest(null)
    },
    setStatusOptions (response) {
      // combine the arrays inside the 'statuses' values for each obj
      const fullList = [].concat(...response.map(issueType => issueType.statuses))
      // Make it unique
      const uniqueStatusSet = new Set()
      this.statuses = fullList.filter(status => {
        if (uniqueStatusSet.has(status.id)) {
          return false
        }
        uniqueStatusSet.add(status.id)
        return true
      })
    }
  }
}



// WEBPACK FOOTER //
// ./src/mixins/requirementModal.js